import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { RegularButton } from './elements/RegularButton'
import { StateMachine } from '../utils/StatusStateMachine'
import { States } from '../config'
import { useModal } from '../hooks/useModal'
import { StatusVerificationModal } from './modals/content/StatusVerification'
import { Deferred } from '../utils/Deferred'
import { useParams, useNavigate } from 'react-router-dom'
import { Status, statusOptions } from './Status'
import {
    useGetParticipantByIdQuery,
    useUpdateParticipantStatusMutation,
} from '../services/participants'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { useEffect } from 'react'

interface ReviewButtonProps {
    participantId: string
}

export default function ReviewButton({ participantId }: ReviewButtonProps) {
    const [open] = useModal()
    const navigate = useNavigate()
    const { '*': splat } = useParams()
    const [updateParticipantStatus] = useUpdateParticipantStatusMutation()
    const { data: participant, isLoading } = useGetParticipantByIdQuery(participantId)

    if (isLoading) {
        return null
    }

    let mouseX: number, mouseY: number

    /**
     * This is a hack to close the tooltip when the mouse leaves the tooltip element.
     */
    useEffect(() => {
        function mouseMove(e: MouseEvent) {
            mouseX = e.clientX
            mouseY = e.clientY
        }

        window.addEventListener('mousemove', mouseMove)

        return () => window.removeEventListener('mousemove', mouseMove)
    })

    const study = participant?.studies[0]?.study
    const value = participant?.studies[0]?.status as unknown as States

    const current = statusOptions.find((item) => item.value === value)

    const stateMachine = new StateMachine(value as States, async (toState: States) => {
        const def = new Deferred<boolean>()
        const selected = statusOptions.find((item) => item.value === toState)
        open({
            content: (
                <StatusVerificationModal
                    onSubmit={onSubmit}
                    participantId={participantId}
                    studyId={study?.id}
                    transition={{ from: current, to: selected }}
                />
            ),
        })

        return def.promise
    })

    async function onSubmit(
        from: States,
        status: States,
        annotation = '',
        studyId = study.id,
        credentials?: { username: string; password: string }
    ) {
        const payload = [
            {
                study_id: studyId,
                from: from,
                patient_id: participantId,
                annotation,
                status,
            },
        ]

        await updateParticipantStatus({
            ...credentials,
            set: payload,
        }).unwrap()

        navigate(`../${status}/${participantId}/${splat}`, { relative: 'route' })

        return true
    }

    async function onStatusChange(value: States) {
        const isSuccessful = await stateMachine.executeTransition(value)
        if (isSuccessful) {
            onSubmit(current?.value, value)
        }
    }

    /**
     * We need to keep a reference to each tooltip so we can open/close it when the mouse enters/leaves dropdown options
     */
    const refs: Record<States, TooltipRefProps> = {
        [States.qualified]: null,
        [States.not_qualified]: null,
        [States.review]: null,
        [States.excluded]: null,
        [States.invited]: null,
        [States.randomised]: null,
    }

    return (
        <div className="flex rounded-md z-10">
            <Listbox
                as="div"
                className="-ml-px block rounded-md shadow-sm"
                value={current?.value}
                onChange={onStatusChange}
            >
                {current && (
                    <Listbox.Button as={RegularButton} className="h-10 inline-flex items-center">
                        <Status status={current.value} />
                        <ChevronDownIcon
                            className="h-5 w-5 ml-2 fill-gray-900"
                            aria-hidden="true"
                        />
                    </Listbox.Button>
                )}

                <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Listbox.Options className="absolute review-dropdown left-0 z-10 mt-2 -mr-1 w-[312px] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="divide-y">
                            {statusOptions
                                .filter(({ value }) => value !== current?.value)
                                .map(({ value }) => {
                                    /**
                                     * We only want to close the tooltip if the mouse is not over the tooltip element
                                     */
                                    function onHideTooltip() {
                                        setTimeout(() => {
                                            if (mouseX && mouseY) {
                                                const tooltip = document
                                                    .elementFromPoint(mouseX, mouseY)
                                                    ?.closest('[role=tooltip]')

                                                if (!tooltip) {
                                                    refs[value]?.close()
                                                }
                                            }
                                        }, 50)
                                    }
                                    return (
                                        <li key={value} id={`${value}-option`}>
                                            <Listbox.Option
                                                as="div"
                                                className="w-full review-dropdown-item trigger hover:bg-gray-100"
                                                value={value}
                                                onMouseEnter={() => refs[value]?.open()}
                                                onMouseLeave={onHideTooltip}
                                                onBlur={onHideTooltip}
                                            >
                                                <div className="p-4">
                                                    <Status status={value} />
                                                </div>
                                            </Listbox.Option>
                                        </li>
                                    )
                                })}
                        </div>
                    </Listbox.Options>

                    {statusOptions.map(({ value, tooltip }) => (
                        <Tooltip
                            ref={(ref) => (refs[value] = ref)}
                            imperativeModeOnly
                            key={`${value}-tooltip`}
                            place="right"
                            clickable
                            anchorSelect={`#${value}-option`}
                        >
                            {tooltip}
                        </Tooltip>
                    ))}
                </Transition>
            </Listbox>
        </div>
    )
}
